import React, { Component } from "react";
import PropTypes from "prop-types";
import ActionLogger from "action-logger";
import { analytics, membershipLens, inventoryEligibilities } from "rtr-constants";
import FitSummary from "components/source/reviews/fit-summary";
import NarrowPDPImagery from "components/source/pdp/narrow-pdp-imagery";
import PDPImagery from "components/source/pdp/pdp-imagery-old";
import AtomCustomerReviewsLink from "components/source/atoms/atom-customer-reviews-link";
import AtomProductCardTitleInfo from "components/source/atoms/atom-product-card-title-info";
import UserProductRatingsContainer from "components/source/user_product_ratings/user-product-ratings-container";
import productRatingsActions from "actions/product-ratings-actions";
import sharedActions from "actions/shared-actions";
import UnlimitedAcquisitionMessaging from "components/source/acquisition/unlimited-acquisition-messaging";
import RTRUpdateAcquisitionMessaging from "components/source/acquisition/rtrupdate-acquisition-messaging";
import Routes from "routes";
import AnalyticsPreviousLocationContextActions from "actions/analytics-previous-location-context-actions";
import AnchorProductAggregateRating from "components/source/products/anchor-product-aggregate-rating";
import reviewsActions from "../../../actions/reviews-actions";
import ProductDrawerHelper from "helpers/product-drawer-helper";
import MembershipHelpers from "helpers/membership-helpers";
import ViewedProductsHelpers from "helpers/viewed-products-helpers";
import membershipBagActions from "actions/membership-bag-actions";
import {
  browserPropType,
  carouselPropType,
  membershipBagPropType,
  productPropType,
  reviewsCoverPhotosPropType,
} from "components/propTypes";
import SwapsFormFields from "components/source/swaps/SwapsFormFields";
import MembershipFormFields from "components/source/membership/membership-form-fields";
import { windowIsMedium } from "helpers/device-helpers";
import HeapHelpers from "helpers/heap-helpers";
import { buildPDPDrawerEvents } from "helpers/heap-pdp-helpers";

class ProductDrawer extends Component {
  static propTypes = {
    acquisitionMessaging: PropTypes.object, // TODO: confirm this PropType
    annotationSuffix: PropTypes.string,
    browser: browserPropType,
    carousel: carouselPropType,
    carouselIndex: PropTypes.number,
    closeProductDrawer: PropTypes.func,
    customHeartLoggingOptions: PropTypes.object,
    dispatch: PropTypes.func,
    displayedModal: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    favorites: PropTypes.arrayOf(PropTypes.string),
    fetchProductsOnApply: PropTypes.bool,
    isHomepageDrawer: PropTypes.bool,
    isMembershipHomepage: PropTypes.bool,
    isOpen: PropTypes.bool,
    isSubmittingMembershipActions: PropTypes.bool,
    lensOverride: PropTypes.string,
    logQuickViewFullDetails: PropTypes.func,
    loggingOptionsForDrawer: PropTypes.object,
    membershipBag: membershipBagPropType,
    membershipPricing: PropTypes.array, // TODO: confirm this PropType
    membershipState: PropTypes.object,
    mobileWidth: PropTypes.bool,
    onAddToBag: PropTypes.func, // does not appear this is used
    onClickUpgradePricingTooltip: PropTypes.func,
    onFetchAvailability: PropTypes.func,
    product: productPropType,
    productDrawer: PropTypes.object,
    productIndex: PropTypes.number,
    reviewsCoverPhotos: reviewsCoverPhotosPropType,
    saveGridContextToStorage: PropTypes.func,
    selectReplacement: PropTypes.func,
    shortlists: PropTypes.arrayOf(PropTypes.object),
    swapDrawer: PropTypes.bool, // Marker for a swaps grid
    upgradeTooltipText: PropTypes.string,
    userData: PropTypes.object,
    workingFilters: PropTypes.object,
  };

  static defaultProps = {
    favorites: [],
    isOpen: true,
    logQuickViewFullDetails: () => {}, // NOSONAR
    saveGridContextToStorage: () => {}, // NOSONAR
  };

  $productDrawer = null;

  componentDidMount() {
    if (this.props.isOpen) {
      this.prepareProductDrawer();
    }
  }

  componentDidUpdate(prevProps) {
    const { product, isOpen } = this.props;
    const { id: productId } = product || {};
    const { product: prevProduct } = prevProps;
    const { id: prevProductId } = prevProduct || {};

    if (isOpen) {
      if (productId !== prevProductId) {
        this.prepareProductDrawer();
        this.scrollIntoView();
      }
    }
  }

  prepareProductDrawer() {
    this.props.dispatch(reviewsActions.clearReviewsCoverPhotos());
    this.props.dispatch(membershipBagActions.clearBagErrors());
    this.requestReviewsCoverPhotos();
    this.saveViewedProductToLocalStorage();
    this.handleOnOpenEventLogging();

    // TODO: consider throttling this call, or at least, allow for a grace
    // period from initial page load where we assume availability is still
    // correct
    this.handleFetchAvailability();
  }

  requestReviewsCoverPhotos = (params = {}) => {
    this.props.dispatch(reviewsActions.requestReviewsCoverPhotos(this.props.product.id, params));
  };

  scrollIntoView() {
    ProductDrawerHelper.scrollIntoView(this.$productDrawer);
  }

  saveViewedProductToLocalStorage() {
    const { membershipBag, product, userData } = this.props;
    const { id: styleName } = product || {};
    const { items = [] } = membershipBag || {};
    const isMember = MembershipHelpers.isSubscriptionMember(userData);
    const membershipBagItems = isMember ? items : [];
    const bagStyleNames = membershipBagItems.map(i => i.styleName);
    const styleIsInBag = bagStyleNames.includes(styleName);

    // only save unhearted items to viewed product list
    if (!this.isFavorited() && isMember && !styleIsInBag) {
      ViewedProductsHelpers.addViewedProductToList(styleName, userData);
    }
  }

  handleOnOpenEventLogging() {
    this.heapLogging();
  }

  heapLogging() {
    const { membershipState, userData, product, workingFilters } = this.props;
    const eventData = buildPDPDrawerEvents(membershipState, userData, product, workingFilters);
    HeapHelpers.addEventProperties(eventData);
    HeapHelpers.fireHeapEvent("open_drawer", eventData);
  }

  hasUnlimitedLens = () => {
    return MembershipHelpers.isUnlimitedLens(this.props.userData);
  };

  hasRTRUpdateLens = () => {
    return MembershipHelpers.isRtrUpdateLens(this.props.userData);
  };

  isRtrUpdateInventoryAvailability = () => {
    return (
      MembershipHelpers.hasInventoryEligibility(this.props.membershipState, inventoryEligibilities.rtr_update) &&
      MembershipHelpers.isSubscriptionMember(this.props.userData)
    );
  };

  isUnlimitedInventoryAvailability = () => {
    return (
      MembershipHelpers.hasInventoryEligibility(this.props.membershipState, inventoryEligibilities.rtr_unlimited) &&
      MembershipHelpers.isSubscriptionMember(this.props.userData)
    );
  };

  isUnlimitedProspecting = () => {
    // If you are a member, you cannot be prospecting
    if (this.isUnlimitedInventoryAvailability()) {
      return false;
    }

    // If we do not pass in the prop, we should default to the lens
    if (!this.props.lensOverride) {
      return this.hasUnlimitedLens();
    }

    // If the prop is there, return true if the prop is unlimited
    return this.props.lensOverride === membershipLens.unlimited;
  };

  isRTRUpdateProspecting = () => {
    // If you are a member, you cannot be prospecting
    if (this.isRtrUpdateInventoryAvailability()) {
      return false;
    }

    // If we do not pass in the lensOverride prop, we should default to the lens
    if (!this.props.lensOverride) {
      return this.hasRTRUpdateLens();
    }

    // If the lensOverride prop is there, return true if the prop is update
    return this.props.lensOverride === membershipLens.RTRUpdate;
  };

  logAction = action => {
    ActionLogger.logAction({
      object_type: "queue",
      action: action,
      style_name: this.props.product.id,
    });
  };

  logInferAction = action => {
    ActionLogger.inferAction({
      ...this.props.loggingOptionsForDrawer,
      object_type: "unlimited_drawer",
      action: action,
      style_name: this.props.product.id,
    });
  };

  closeModal = () => {
    this.props.dispatch(sharedActions.displayModal(false));
  };

  openModal = modalName => {
    this.props.dispatch(sharedActions.displayModal(modalName));
  };

  isFavorited = () => {
    const { favorites = [], product } = this.props;
    const { id: productId } = product || {};

    return favorites.includes(productId);
  };

  fetchUserProductRatings = () => {
    const { dispatch } = this.props;

    dispatch(productRatingsActions.loadUserProductRatings());
  };

  isHeartEducationTooltipTriggered() {
    const { favorites = [], membershipState } = this.props;
    // NW [EXPLANATION] 5/30/19: users in term 0, or users in any term with no hearts, will see this education tooltip
    const { membershipTermNumber = 0 } = membershipState || {};
    return membershipTermNumber === 0 || favorites.length <= 0;
  }

  renderCustomerInteractions = () => {
    // do not render on prospect grids
    if (this.isUnlimitedProspecting() || this.isRTRUpdateProspecting()) {
      return;
    }

    // NW [EXPLANATION] 1/13/20: conditionally override object_type if we are on a grid
    let object_type;
    const { isMembershipHomepage, loggingOptionsForDrawer } = this.props;
    if (isMembershipHomepage) {
      ({ object_type } = loggingOptionsForDrawer || {});
    } else {
      object_type = analytics.OBJECT_TYPE.GRID;
    }

    const customHeartLoggingOptions = {
      ...this.props.loggingOptionsForDrawer,
      ...this.props.customHeartLoggingOptions,
      object_type,
    };

    return (
      <div className="flex justify-between product-rating-container__wrapper">
        {this.renderReviews()}
        <UserProductRatingsContainer
          customHeartLoggingOptions={customHeartLoggingOptions}
          isHeartEducationTooltipTriggered={this.isHeartEducationTooltipTriggered()}
          product={this.props.product}
          location={analytics.ACTION_LOCATIONS.DRAWER}
        />
      </div>
    );
  };

  getReviewSummary = () => {
    const { swapDrawer, product, productDrawer } = this.props;

    // Reading reviews summary from product if swap grid
    if (swapDrawer) {
      return product.reviewSummary;
    }

    if (!this.isUnlimitedProspecting() && !this.isRTRUpdateProspecting()) {
      const { reviews: { summary } = {} } = productDrawer || {};
      return summary;
    }
  };

  renderReviews = () => {
    const reviewSummary = this.getReviewSummary();

    if (!reviewSummary) {
      return null;
    }

    const { product } = this.props;
    const redirectUrl = `${this.pdpUrl()}`;
    const reviews = { averageRating: reviewSummary.averageStarRating, count: reviewSummary.reviewCount };
    return (
      <div className="md:pr-40">
        <AnchorProductAggregateRating productId={product.id} reviews={reviews} redirectUrl={redirectUrl} />
      </div>
    );
  };

  shouldRenderNarrowPDPImagery = () => {
    const { browser: { isMobileViewport } = {} } = this.props;
    return isMobileViewport || this.props.mobileWidth || windowIsMedium();
  };

  handleLogInferFullDetails = () => {
    const {
      loggingOptionsForDrawer,
      isMembershipHomepage,
      logQuickViewFullDetails,
      saveGridContextToStorage,
      product,
      productIndex,
    } = this.props;
    const { carousel_id, request_id, nth_style, nth_carousel } = loggingOptionsForDrawer || {};
    const carouselContext = {
      style_name: product.id,
      carousel_id,
      request_id,
      nth_style,
      nth_carousel,
    };

    // NW [EXPLANATION] 4/23/19: Data Science wants to know which carousel the user came from, after they land on the PDP and start hearting/adding to bag.
    // To persist this carouselContext to the PDP, we store it in localStorage. When the PDP mounts, we will save it in Redux and clear it from localStorage, so subsequent navigation is unaffected.
    AnalyticsPreviousLocationContextActions.savePreviousLocationContextToStorage(carouselContext);

    if (isMembershipHomepage) {
      logQuickViewFullDetails(carouselContext);
    } else {
      this.logInferAction("drawer_pdp");
      /** Data Science wants to know when a user came from a Grid and lands on a PDP. To persist the location we store it in localStorage. When the PDP mounts we save it to Redux and clear it for LS **/
      saveGridContextToStorage(productIndex);
    }
  };

  handleCloseProductDrawer = () => {
    const { closeProductDrawer, swapDrawer, dispatch, productDrawer: { parentElement } = {} } = this.props;

    this.logAction("cancel_avail_item_detail"); // likely unneeded
    closeProductDrawer();

    if (!swapDrawer) {
      dispatch(membershipBagActions.clearBagErrors());
    }

    if (parentElement) {
      ProductDrawerHelper.scrollIntoView(parentElement);
    }
  };

  handleMembershipPickWayfindClick = () => {
    window.location.href = `${this.pdpUrl()}?lens=classic`;
  };

  shouldRenderCustomerReviewsLink = () => {
    const { browser, swapDrawer } = this.props;
    const { isMobileViewport = false } = browser || {};

    // NW [EXPLANATION] 2/6/19: this link should only be rendered in Swap Drawer or Mobile drawer. Desktop product drawer will show featured review thumbnail instead.
    return swapDrawer || isMobileViewport;
  };

  getCoverPhotoId() {
    const { product, reviewsCoverPhotos } = this.props;

    const firstReviewCoverPhoto = reviewsCoverPhotos?.[0]?.id;

    if (firstReviewCoverPhoto) return firstReviewCoverPhoto;

    return product?.reviews?.find(r => r.coverPhotoId)?.coverPhotoId;
  }

  renderCustomerReviewsLink() {
    if (!this.shouldRenderCustomerReviewsLink()) return;

    const { productDrawer } = this.props;
    const customerPhotosAvailable = ProductDrawerHelper.getOnlyReviewsWithPhotos(productDrawer?.reviews).length > 0;

    const { momentCount } = productDrawer?.reviews || {};
    const coverPhotoId = this.getCoverPhotoId();

    if (!coverPhotoId && !customerPhotosAvailable) return;

    return <AtomCustomerReviewsLink className="product-drawer__links-customer-reviews" momentCount={momentCount} />;
  }

  pdpUrl = () => {
    const { product } = this.props;
    const { id: productId } = product || {};

    return Routes.PDP.RouteById(productId);
  };

  renderDrawerLinks = () => {
    const { swapDrawer } = this.props;

    if (this.isUnlimitedProspecting() || this.isRTRUpdateProspecting()) {
      return null;
    }

    let lensQueryParam = "";

    if (this.isUnlimitedInventoryAvailability()) {
      lensQueryParam = "?lens=unlimited";
    } else if (this.isRtrUpdateInventoryAvailability()) {
      lensQueryParam = "?lens=rtrupdate";
    }

    let fullDetails = null;
    if (!swapDrawer) {
      fullDetails = (
        <a
          href={`${this.pdpUrl()}${lensQueryParam}`}
          onClick={this.handleLogInferFullDetails}
          className="product-drawer__links-full-details universal-small--semibold universal-underline">
          View Full Details
        </a>
      );
    }

    return (
      <div className="product-drawer__links">
        {fullDetails}
        {this.renderCustomerReviewsLink()}
      </div>
    );
  };

  renderUnlimitedAcquisitionMessaging = () => {
    return (
      <UnlimitedAcquisitionMessaging
        product={this.props.product}
        loggingLocation={analytics.ACTION_LOCATIONS.DRAWER}
        acquisitionMessaging={this.props.acquisitionMessaging}
        dispatch={this.props.dispatch}
      />
    );
  };

  renderRTRUpdateAcquisitionMessaging = () => {
    return (
      <RTRUpdateAcquisitionMessaging
        product={this.props.product}
        loggingLocation={analytics.ACTION_LOCATIONS.DRAWER}
        acquisitionMessaging={this.props.acquisitionMessaging}
        dispatch={this.props.dispatch}
      />
    );
  };

  renderMembershipFormFields = () => {
    const { userData } = this.props;

    return (
      <MembershipFormFields
        analyticsLoggingContext={this.props.loggingOptionsForDrawer}
        carouselIndex={this.props.carouselIndex}
        dispatch={this.props.dispatch}
        displayedModal={this.props.displayedModal}
        fetchProductsOnApply={this.props.fetchProductsOnApply}
        handleWayfindCtaClick={this.handleMembershipPickWayfindClick}
        isHomepageDrawer={this.props.isHomepageDrawer}
        isSubmittingMembershipActions={this.props.isSubmittingMembershipActions}
        loggingLocation={analytics.ACTION_LOCATIONS.DRAWER}
        membershipBag={this.props.membershipBag}
        membershipState={this.props.membershipState}
        membershipToggleRequired={true}
        onAddToBagFailure={this.handleFetchAvailability}
        pdpUrl={this.pdpUrl()}
        product={this.props.product}
        userData={userData}
        workingFilters={this.props.workingFilters}
      />
    );
  };

  renderProductImagery = () => {
    if (this.shouldRenderNarrowPDPImagery()) {
      return (
        <NarrowPDPImagery
          product={this.props.product}
          userData={this.props.userData}
          workingFilters={this.props.workingFilters}
        />
      );
    }

    const { productDrawer } = this.props;
    const { reviews } = productDrawer || {};
    return (
      <PDPImagery
        isCloudZoomEnabled={true}
        product={this.props.product}
        reviews={reviews}
        isProductDrawer={true}
        userData={this.props.userData}
        workingFilters={this.props.workingFilters}
        reviewsCoverPhotos={this.props.reviewsCoverPhotos}
      />
    );
  };

  renderFitSummary = () => {
    const reviewSummary = this.getReviewSummary();

    if (!reviewSummary) {
      return null;
    }

    return (
      <div className="product-drawer__fit-summary">
        <FitSummary fitCounts={reviewSummary?.fitCounts} />
      </div>
    );
  };

  renderFormFields = () => {
    if (this.props.swapDrawer) {
      return this.renderSwapFields();
    }

    if (MembershipHelpers.isActiveMembership(this.props.membershipState)) {
      return this.renderMembershipFormFields();
    }

    if (this.isUnlimitedProspecting()) {
      return this.renderUnlimitedAcquisitionMessaging();
    }

    if (this.isRTRUpdateProspecting()) {
      return this.renderRTRUpdateAcquisitionMessaging();
    }
  };

  // Only rendered if a swap drawer
  renderSwapFields = () => {
    return (
      // TODO: define propTypes for SwapsFormFields
      // eslint-disable-next-line react/jsx-props-no-spreading
      <SwapsFormFields product={this.props.product} selectReplacement={this.props.selectReplacement} {...this.props} />
    );
  };

  handleFetchAvailability = () => {
    const { onFetchAvailability, product: { id } = {} } = this.props;

    if (!onFetchAvailability) {
      return;
    }

    onFetchAvailability(id);
  };

  render() {
    if (!this.props.isOpen) {
      return null;
    }

    return (
      <section className="product-drawer" ref={node => (this.$productDrawer = node)}>
        <button className="product-drawer__close-button" onClick={this.handleCloseProductDrawer}>
          <div className="close"></div>
        </button>
        <aside className="product-drawer__images">{this.renderProductImagery()}</aside>
        <aside className="product-drawer__aside">
          <section className="product-drawer__details">
            {this.renderCustomerInteractions()}
            <div className="product-drawer__info">
              <AtomProductCardTitleInfo
                lensOverride={this.props.lensOverride}
                product={this.props.product}
                userData={this.props.userData}
                hidePrice={this.props.swapDrawer}
                annotationSuffix={this.props.annotationSuffix}
                upgradeTooltipText={this.props.upgradeTooltipText}
                onClickUpgradeTooltip={this.props.onClickUpgradePricingTooltip}
                membershipPricing={this.props.membershipPricing}
              />
            </div>
          </section>
          <div className="product-drawer__form-fields">{this.renderFormFields()}</div>
          {this.renderFitSummary()}
          {this.renderDrawerLinks()}
        </aside>
      </section>
    );
  }
}

export default ProductDrawer;
